import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import API from '../services/api';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (filters: ExportFilters, format: 'csv' | 'excel') => void;
}

export interface ExportFilters {
  countries?: string[];
  dateFrom?: string;
  dateTo?: string;
  violationTypes?: string[];
  perpetratorTypes?: string[];
  indigenousGroups?: string[];
  includeVictims?: boolean;
  includePerpetrators?: boolean;
  includeDocuments?: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, onExport }) => {
  const format = 'csv'; // Fixed to CSV format only
  const [filters, setFilters] = useState<ExportFilters>({
    includeVictims: true,
    includePerpetrators: true,
    includeDocuments: false
  });

  // Available options loaded from API
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [availableIndigenousGroups, setAvailableIndigenousGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const violationTypes = [
    { value: 'KILLING', label: 'Killing' },
    { value: 'CRIMINALIZATION', label: 'Criminalization' },
    { value: 'FORCED_DISPLACEMENT', label: 'Forced Displacement' },
    { value: 'LAND_GRABBING', label: 'Land Grabbing' },
    { value: 'ENVIRONMENTAL_DESTRUCTION', label: 'Environmental Destruction' },
    { value: 'CULTURAL_DESTRUCTION', label: 'Cultural Destruction' },
    { value: 'OTHER', label: 'Other' }
  ];

  const perpetratorTypes = [
    { value: 'STATE_ACTOR', label: 'State Actor' },
    { value: 'PRIVATE_CORPORATION', label: 'Private Corporation' },
    { value: 'PARAMILITARY', label: 'Paramilitary' },
    { value: 'CRIMINAL_GROUP', label: 'Criminal Group' },
    { value: 'INDIVIDUAL', label: 'Individual' },
    { value: 'UNKNOWN', label: 'Unknown' },
    { value: 'OTHER', label: 'Other' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchFilterOptions();
    }
  }, [isOpen]);

  const fetchFilterOptions = async () => {
    setLoading(true);
    try {
      const [analyticsResponse] = await Promise.all([
        API.get('/api/analytics/dashboard')
      ]);

      // Extract unique countries
      const countries = analyticsResponse.data.casesByCountry.map((c: any) => c.country);
      setAvailableCountries(countries);

      // Extract indigenous groups
      const groups = analyticsResponse.data.indigenousGroups?.topGroups?.map((g: any) => g.group) || [];
      setAvailableIndigenousGroups(groups);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    onExport(filters, format);
    onClose();
  };

  const handleCountryToggle = (country: string) => {
    const current = filters.countries || [];
    if (current.includes(country)) {
      setFilters({ ...filters, countries: current.filter(c => c !== country) });
    } else {
      setFilters({ ...filters, countries: [...current, country] });
    }
  };

  const handleViolationTypeToggle = (type: string) => {
    const current = filters.violationTypes || [];
    if (current.includes(type)) {
      setFilters({ ...filters, violationTypes: current.filter(t => t !== type) });
    } else {
      setFilters({ ...filters, violationTypes: [...current, type] });
    }
  };

  const handlePerpetratorTypeToggle = (type: string) => {
    const current = filters.perpetratorTypes || [];
    if (current.includes(type)) {
      setFilters({ ...filters, perpetratorTypes: current.filter(t => t !== type) });
    } else {
      setFilters({ ...filters, perpetratorTypes: [...current, type] });
    }
  };

  const handleIndigenousGroupToggle = (group: string) => {
    const current = filters.indigenousGroups || [];
    if (current.includes(group)) {
      setFilters({ ...filters, indigenousGroups: current.filter(g => g !== group) });
    } else {
      setFilters({ ...filters, indigenousGroups: [...current, group] });
    }
  };

  const resetFilters = () => {
    setFilters({
      includeVictims: true,
      includePerpetrators: true,
      includeDocuments: false
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Custom Data Export</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date Range
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">From</label>
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">To</label>
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm"
                />
              </div>
            </div>
          </div>

          {/* Countries */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Countries / Regions ({filters.countries?.length || 0} selected)
            </label>
            {loading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading countries...</p>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div className="space-y-1">
                  {availableCountries.map((country) => (
                    <label key={country} className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={filters.countries?.includes(country) || false}
                        onChange={() => handleCountryToggle(country)}
                        className="mr-2"
                      />
                      {country}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Violation Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Incident Types ({filters.violationTypes?.length || 0} selected)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {violationTypes.map((type) => (
                <label key={type.value} className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={filters.violationTypes?.includes(type.value) || false}
                    onChange={() => handleViolationTypeToggle(type.value)}
                    className="mr-2"
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>

          {/* Perpetrator Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Perpetrator Organizations ({filters.perpetratorTypes?.length || 0} selected)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {perpetratorTypes.map((type) => (
                <label key={type.value} className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={filters.perpetratorTypes?.includes(type.value) || false}
                    onChange={() => handlePerpetratorTypeToggle(type.value)}
                    className="mr-2"
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>

          {/* Indigenous Groups */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Indigenous Groups Affected ({filters.indigenousGroups?.length || 0} selected)
            </label>
            {loading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading indigenous groups...</p>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div className="space-y-1">
                  {availableIndigenousGroups.map((group) => (
                    <label key={group} className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={filters.indigenousGroups?.includes(group) || false}
                        onChange={() => handleIndigenousGroupToggle(group)}
                        className="mr-2"
                      />
                      {group}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Include Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Include in Export
            </label>
            <div className="space-y-2">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={filters.includeVictims}
                  onChange={(e) => setFilters({ ...filters, includeVictims: e.target.checked })}
                  className="mr-2"
                />
                Victim Information
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={filters.includePerpetrators}
                  onChange={(e) => setFilters({ ...filters, includePerpetrators: e.target.checked })}
                  className="mr-2"
                />
                Perpetrator Information
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={filters.includeDocuments}
                  onChange={(e) => setFilters({ ...filters, includeDocuments: e.target.checked })}
                  className="mr-2"
                />
                Document Metadata
              </label>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Export Summary (CSV Format)</h3>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <p>Countries: <span className="font-medium">{filters.countries?.length || 'All'}</span></p>
              <p>Incident Types: <span className="font-medium">{filters.violationTypes?.length || 'All'}</span></p>
              <p>Perpetrator Types: <span className="font-medium">{filters.perpetratorTypes?.length || 'All'}</span></p>
              <p>Indigenous Groups: <span className="font-medium">{filters.indigenousGroups?.length || 'All'}</span></p>
              {filters.dateFrom && <p>Date From: <span className="font-medium">{filters.dateFrom}</span></p>}
              {filters.dateTo && <p>Date To: <span className="font-medium">{filters.dateTo}</span></p>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <button
            onClick={resetFilters}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Reset Filters
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark"
            >
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
